/**
 * CallProtocol — Voice & Video build-plan step 2.
 *
 * libp2p protocol registration for `/surface-mesh/call/1.0.0`. This is the
 * direct peer-to-peer stream over which SDP offers + ICE candidates flow
 * during call setup. The MLS-encrypted ring envelope (step 3) carries the
 * caller's peerId; the callee dials back over this protocol.
 *
 * The protocol carries a sequence of JSON messages:
 *
 *   { kind: 'sdp-offer', callId, sdp }
 *   { kind: 'sdp-answer', callId, sdp }
 *   { kind: 'ice', callId, candidate }
 *   { kind: 'bye', callId }
 *
 * Each side reads + writes in lockstep. Stream lifetime = call lifetime
 * (until the call enters a terminal state).
 *
 * Architecture seams:
 *
 *   - In production, the libp2p handle comes from `ctx.meshNode.libp2p()`.
 *     We register a stream handler on `start()` and tear it down on `stop()`.
 *   - In tests, an `InMemoryCallTransport` simulates two peers exchanging
 *     messages without a real libp2p instance. CallProtocol uses the same
 *     interface, so production + tests share the orchestration code.
 *   - The actual WebRTC `RTCPeerConnection` lives elsewhere (build-plan
 *     step 4); CallProtocol only delivers signaling bytes.
 */

import type { AppContext } from '../app';

export const CALL_PROTOCOL_ID = '/surface-mesh/call/1.0.0';

export type CallSignalingMessage =
  | { kind: 'sdp-offer'; callId: string; sdp: string }
  | { kind: 'sdp-answer'; callId: string; sdp: string }
  | { kind: 'ice'; callId: string; candidate: string }
  | { kind: 'bye'; callId: string; reason?: string };

export type CallSignalingHandler = (peer: string, msg: CallSignalingMessage) => Promise<void> | void;

/**
 * Transport-agnostic interface for call signaling. The real libp2p path
 * and the in-memory test path both implement this.
 */
export interface CallSignalingTransport {
  /** Register the inbound handler. Required before start(). */
  setHandler(fn: CallSignalingHandler): void;
  start(): Promise<void>;
  stop(): Promise<void>;
  /** Send a signaling message to a known peer. */
  send(peer: string, msg: CallSignalingMessage): Promise<void>;
  readonly started: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// IN-MEMORY TRANSPORT (tests + single-process demos)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Simulates a libp2p stream by routing messages through a shared bus. Each
 * CallProtocol instance registers itself on the bus by its peerId; sends
 * route to the destination peer's handler synchronously (within the same
 * JS event loop tick — actual production has real network latency).
 */
export class InMemoryCallBus {
  static #global = new InMemoryCallBus();
  static global(): InMemoryCallBus { return InMemoryCallBus.#global; }

  #peers = new Map<string, CallSignalingHandler>();

  register(peerId: string, handler: CallSignalingHandler): () => void {
    this.#peers.set(peerId, handler);
    return () => { this.#peers.delete(peerId); };
  }

  async deliver(from: string, to: string, msg: CallSignalingMessage): Promise<void> {
    const handler = this.#peers.get(to);
    if (handler === undefined) {
      throw new Error(`InMemoryCallBus: peer ${to} not connected`);
    }
    await handler(from, msg);
  }

  reset(): void { this.#peers.clear(); }
}

export class InMemoryCallTransport implements CallSignalingTransport {
  readonly #bus: InMemoryCallBus;
  readonly #ownPeerId: string;
  #handler: CallSignalingHandler | null = null;
  #unsub: (() => void) | null = null;
  #started = false;

  constructor(ownPeerId: string, bus: InMemoryCallBus = InMemoryCallBus.global()) {
    this.#bus = bus;
    this.#ownPeerId = ownPeerId;
  }

  get started(): boolean { return this.#started; }

  setHandler(fn: CallSignalingHandler): void { this.#handler = fn; }

  async start(): Promise<void> {
    if (this.#started) return;
    if (this.#handler === null) {
      throw new Error('InMemoryCallTransport.start: setHandler must be called first');
    }
    this.#unsub = this.#bus.register(this.#ownPeerId, this.#handler);
    this.#started = true;
  }

  async stop(): Promise<void> {
    if (!this.#started) return;
    this.#unsub?.();
    this.#unsub = null;
    this.#started = false;
  }

  async send(peer: string, msg: CallSignalingMessage): Promise<void> {
    if (!this.#started) throw new Error('InMemoryCallTransport: not started');
    await this.#bus.deliver(this.#ownPeerId, peer, msg);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// libp2p TRANSPORT (production, browser-only edges)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Real-libp2p implementation. Registers a stream handler on the libp2p
 * instance owned by `ctx.meshNode`. Stream-per-call: opening a stream to
 * a peer initiates the handshake; the inbound handler reads messages
 * one-by-one and dispatches to the registered CallSignalingHandler.
 *
 * Browser-only edge: the actual stream codec (length-prefixed JSON, or
 * `it-length-prefixed` + `it-pb` per libp2p convention) lands in build-plan
 * step 3.1. The InMemoryCallTransport is what tests use; the libp2p path
 * here is the seam for browser wire-up.
 */
export class Libp2pCallTransport implements CallSignalingTransport {
  readonly #ctx: AppContext;
  #handler: CallSignalingHandler | null = null;
  #started = false;
  /** Active outbound streams keyed by remote peerId. */
  readonly #streams = new Map<string, unknown>();

  constructor(ctx: AppContext) {
    this.#ctx = ctx;
  }

  get started(): boolean { return this.#started; }

  setHandler(fn: CallSignalingHandler): void { this.#handler = fn; }

  async start(): Promise<void> {
    if (this.#started) return;
    if (this.#handler === null) {
      throw new Error('Libp2pCallTransport.start: setHandler must be called first');
    }
    const libp2p = this.#ctx.meshNode.libp2p() as {
      handle?: (proto: string, fn: (params: { connection: { remotePeer: { toString(): string } }; stream: unknown }) => void) => Promise<void>;
    } | null;
    if (libp2p === null || typeof libp2p.handle !== 'function') {
      throw new Libp2pCallTransportUnavailable(
        'Mesh node has no libp2p.handle — running on MockMeshNode? Use InMemoryCallTransport instead.',
      );
    }
    await libp2p.handle(CALL_PROTOCOL_ID, ({ connection, stream }) => {
      const remote = connection.remotePeer.toString();
      // The stream-reader implementation lives in build-plan step 3.1.
      // For Phase 8 v1, we register the handler so other code can see
      // that this protocol is reachable; actual byte-level codec lands
      // when the WebRTC orchestration is wired.
      void stream;
      void remote;
    });
    this.#started = true;
  }

  async stop(): Promise<void> {
    if (!this.#started) return;
    const libp2p = this.#ctx.meshNode.libp2p() as {
      unhandle?: (proto: string) => Promise<void>;
    } | null;
    if (libp2p !== null && typeof libp2p.unhandle === 'function') {
      try { await libp2p.unhandle(CALL_PROTOCOL_ID); } catch { /* best-effort */ }
    }
    this.#streams.clear();
    this.#started = false;
  }

  async send(peer: string, msg: CallSignalingMessage): Promise<void> {
    if (!this.#started) throw new Error('Libp2pCallTransport: not started');
    // Open or reuse stream; for Phase 8 v1, this is a deferred edge that
    // the live WebRTC orchestration (step 3.1) wires.
    void peer; void msg;
    throw new Libp2pCallTransportUnavailable(
      'Libp2pCallTransport.send: byte-codec lands in build-plan step 3.1',
    );
  }
}

export class Libp2pCallTransportUnavailable extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'Libp2pCallTransportUnavailable';
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// PROTOCOL OWNER — wires a transport into call lifecycle hooks
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Top-level orchestrator that wires a `CallSignalingTransport` to call-
 * lifecycle callbacks. CallManager constructs one of these when it needs
 * to actually exchange SDP/ICE — for v1 of Phase 8 it's only invoked from
 * tests + manual browser builds.
 */
export class CallProtocol {
  readonly #transport: CallSignalingTransport;
  #onSignal: ((peer: string, msg: CallSignalingMessage) => Promise<void> | void) | null = null;

  constructor(transport: CallSignalingTransport) {
    this.#transport = transport;
    this.#transport.setHandler(async (peer, msg) => {
      if (this.#onSignal === null) return;
      await this.#onSignal(peer, msg);
    });
  }

  onSignal(fn: (peer: string, msg: CallSignalingMessage) => Promise<void> | void): void {
    this.#onSignal = fn;
  }

  async start(): Promise<void> { await this.#transport.start(); }
  async stop(): Promise<void> { await this.#transport.stop(); }

  async sendOffer(peer: string, callId: string, sdp: string): Promise<void> {
    await this.#transport.send(peer, { kind: 'sdp-offer', callId, sdp });
  }

  async sendAnswer(peer: string, callId: string, sdp: string): Promise<void> {
    await this.#transport.send(peer, { kind: 'sdp-answer', callId, sdp });
  }

  async sendIce(peer: string, callId: string, candidate: string): Promise<void> {
    await this.#transport.send(peer, { kind: 'ice', callId, candidate });
  }

  async sendBye(peer: string, callId: string, reason?: string): Promise<void> {
    await this.#transport.send(peer, reason !== undefined ? { kind: 'bye', callId, reason } : { kind: 'bye', callId });
  }
}
